import { Calendar, Droplets, Leaf, Sparkles, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatments = [
  {
    name: 'The Vitality Anchor',
    subtitle: 'Hair Loss & Thinning',
    description: 'Designed for weak follicles and those seeking density. A high-performance treatment using stimulatory Japanese botanicals and deep tissue scalp massage. We focus on clearing buildup and encouraging a thicker, stronger growth cycle.',
    icon: '💪'
  },
  {
    name: 'The Pure Balance',
    subtitle: 'Oily & Sweaty Scalp',
    description: 'Designed for sebum overproduction and "heavy" feeling hair. A detoxifying reset that uses volcanic clays and cooling mint to regulate oil glands. This treatment removes excess sebum, leaving the scalp feeling light, airy, and refreshed.',
    icon: '⚖️'
  },
  {
    name: 'The Dewy Calm',
    subtitle: 'Dry & Sensitive Scalp',
    description: 'Designed for itchiness, tightness, and redness. A soothing treatment for a thirsty scalp. We use ceramide-rich infusions and anti-inflammatory extracts to heal the skin barrier and restore hydration.',
    icon: '💧'
  },
  {
    name: 'The Clarity Reset',
    subtitle: 'Dandruff & Flaking',
    description: 'Designed for visible flakes and buildup. A deep but gentle exfoliation using enzymes to break down dry or oily flakes, followed by a microbial-balancing rinse to ensure the scalp stays clear and healthy.',
    icon: '✨'
  },
];

const addOns = [
  {
    name: 'Eye Hydration Pads',
    description: 'Cooling, hydrating eye pads to reduce puffiness and refresh tired eyes during your treatment.'
  },
  {
    name: 'LED Light Therapy',
    description: '20 minutes of LED light therapy on face or scalp to boost collagen, reduce inflammation, and enhance results.'
  },
  {
    name: 'Face Hydration Ampoules',
    description: 'Intensive hydration ampoules to plump and nourish your skin while you relax.'
  },
];

const includedFeatures = [
  'Harmonising Health Teas',
  'Professional Scalp Consultation',
  'Japanese-Inspired Waterfall Cleanse',
  'Targeted Scalp Treatment',
  'Relaxing Head & Neck Massage',
  'Blowwave & Style Included'
];

export default function WaterfallHeadSpa() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="waterfall-head-spa-page">
      <SEO page="wellness" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1717160675489-7779f2c91999?w=1920&h=800&fit=crop"
            alt="Luxury Waterfall Head Spa treatment Camberwell"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#4A5D44] text-white rounded-full px-4 py-2 mb-6">
              <Droplets className="w-4 h-4" />
              <span className="text-sm font-medium">Japanese-Inspired Luxury</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-4">
              Signature Artisan Waterfall Head Spa
            </h1>
            <p className="text-xl text-[#8D7B68] italic mb-6">
              "The Ultimate Luxury Hair & Scalp Experience"
            </p>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Indulge in our signature Japanese-inspired therapeutic ritual. The Artisan Waterfall Head Spa combines clinical scalp health with deep relaxation — leaving you with a healthy scalp and beautifully styled hair.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-6 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Book Your Experience
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-[#4A5D44] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-medium mb-2">Every Head Spa Includes</h2>
            <p className="text-white/70">A complete luxury experience from start to finish</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            {includedFeatures.map((feature) => (
              <div key={feature} className="bg-white/10 rounded-lg p-4">
                <p className="text-sm font-medium">{feature}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-white/80 text-lg">
            You won't need to dry your own hair — our professional stylists complete your service with a beautiful blowwave and style.
          </p>
        </div>
      </section>

      {/* The Experience */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="text-3xl">🌊</span>
                <p className="text-sm uppercase tracking-widest text-[#4A5D44]">The Experience</p>
              </div>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                The Waterfall Experience
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Unlike a standard wash, our Artisan Waterfall provides a continuous, rhythmic flow of warm water over the scalp. This Japanese-inspired technique delivers the ultimate in scalp care and relaxation.
              </p>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
                  <h4 className="font-medium text-[#2C2420] mb-2">Harmonising Health Teas</h4>
                  <p className="text-sm text-[#5D4A3A]">Begin your experience with a selection of wellness teas to relax and prepare your mind.</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
                  <h4 className="font-medium text-[#2C2420] mb-2">Professional Scalp Consultation</h4>
                  <p className="text-sm text-[#5D4A3A]">Our specialists analyse your scalp to customise your treatment for optimal results.</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
                  <h4 className="font-medium text-[#2C2420] mb-2">Blowwave & Style Included</h4>
                  <p className="text-sm text-[#5D4A3A]">Leave looking and feeling your best — our stylists finish with a professional blowwave and style.</p>
                </div>
              </div>
            </div>
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/fmf8xi6j_head%20massage.jpg"
                alt="Luxury waterfall head spa treatment"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Scalp Treatments */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-3xl">🌿</span>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44]">Targeted Solutions</p>
            </div>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              Customised Scalp Treatments
            </h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Every head spa begins with a professional scalp consultation. We'll recommend the perfect treatment based on your scalp's unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {treatments.map((treatment) => (
              <div 
                key={treatment.name}
                className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 hover:border-[#4A5D44]/50 transition-colors"
              >
                <div className="text-4xl mb-4">{treatment.icon}</div>
                <h3 className="text-2xl font-medium text-[#2C2420] mb-1">{treatment.name}</h3>
                <p className="text-[#4A5D44] font-medium mb-4">{treatment.subtitle}</p>
                <p className="text-[#5D4A3A]">{treatment.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Hair Treatment */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 aspect-[4/5] rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1666374554058-bc483cbb9695?w=600&h=750&fit=crop"
                alt="Healthy scalp close up treatment"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="text-3xl">💎</span>
                <p className="text-sm uppercase tracking-widest text-[#8D7B68]">Intense Repair</p>
              </div>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Deep Hair Treatment
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Upgrade to our Total Crown Immersion to include an intensive hair repair treatment. While the Waterfall focuses on the scalp, this addition focuses on the hair fibre itself.
              </p>
              <div className="space-y-6">
                <div className="border-l-4 border-[#4A5D44] pl-6">
                  <h4 className="font-medium text-[#2C2420] mb-2">Molecular Bond Repair</h4>
                  <p className="text-[#5D4A3A]">A high-concentration protein and moisture infusion that penetrates the cortex to repair damage from heat, colour, or environmental stress.</p>
                </div>
                <div className="border-l-4 border-[#8D7B68] pl-6">
                  <h4 className="font-medium text-[#2C2420] mb-2">The Silk Finish</h4>
                  <p className="text-[#5D4A3A]">We seal the hair cuticle under the Waterfall to lock in shine and eliminate frizz, leaving your hair feeling like liquid silk.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-3xl">✨</span>
              <p className="text-sm uppercase tracking-widest text-[#D4C4B0]">Enhance Your Experience</p>
            </div>
            <h2 className="text-4xl md:text-5xl font-medium mb-6">
              Signature Add-Ons
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {addOns.map((addon) => (
              <div 
                key={addon.name}
                className="bg-white/10 backdrop-blur p-8 rounded-lg border border-white/20"
              >
                <h3 className="text-xl font-medium text-white mb-3">{addon.name}</h3>
                <p className="text-[#BCAAA4]">{addon.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Book Your Experience</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              Investment in Your Hair & Scalp
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-white p-8 rounded-lg border-2 border-[#D4C4B0] hover:border-[#4A5D44] transition-colors text-center">
              <h3 className="text-2xl font-medium text-[#2C2420] mb-2">Signature Artisan Waterfall Head Spa</h3>
              <p className="text-[#5D4A3A] mb-2">60 Minutes</p>
              <p className="text-sm text-[#8D7B68] mb-4">Includes scalp treatment, massage & blowwave</p>
              <p className="text-4xl font-medium text-[#4A5D44] mb-6">$180</p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full py-4">
                  Book Now
                </Button>
              </a>
            </div>
            <div className="bg-[#4A5D44] p-8 rounded-lg text-white text-center relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-[#8D7B68] text-white text-xs px-3 py-1 rounded-full">
                Most Popular
              </div>
              <h3 className="text-2xl font-medium mb-2">The "Total Crown" Immersion</h3>
              <p className="text-white/80 mb-2">90 Minutes</p>
              <p className="text-sm text-white/60 mb-4">Includes Deep Hair Repair Treatment</p>
              <p className="text-4xl font-medium mb-6">$250</p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-white text-[#4A5D44] hover:bg-[#F5F0EB] rounded-full py-4">
                  Book Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-[#2C2420] mb-6">
            Ready to Relax?
          </h2>
          <p className="text-lg text-[#5D4A3A] mb-8 max-w-2xl mx-auto">
            Book your full immersion experience and leave feeling completely relaxed, refreshed, and beautifully styled.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg">
              <Droplets className="w-5 h-5 mr-2" />
              Book Your Waterfall Experience
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
